import React from "react";
import { AddonParams, BaseAddon } from "../base/BaseAddon";
import {
  StepChangeEvent,
  StepType,
  UploaderSubmissionData,
  ValidationError,
  ValidationErrorCode,
  ValidationResult,
  ValidationWarning,
  ValidationWarningCode
} from "../../types/types";
import toastService from "../../../../../core/toaster/ToasterService";
import { TOASTER_TOAST_TIME } from "../../../../../core/toaster/Toaster_model";
import { ValidateDataView } from "./ValidateDataAddon_view";
import { NoDataFound } from "../../components/NotFound/NoDataFound";

interface ValidateDataAddonParams<T extends string = string> extends AddonParams<T> {
  rowHook?: (row: any, addError: (fieldKey: string, error: any) => void, table: any[]) => any | Promise<any>;
  tableHook?: (
    table: any[],
    addError: (rowIndex: number, fieldKey: string, error: any) => void
  ) => any[] | Promise<any[]>;
  registeredAddons: BaseAddon[];
  onSubmit?: (data: UploaderSubmissionData<any>) => void | Promise<any>;
}

export class ValidateDataAddon<T extends string = string> extends BaseAddon<T> {
  private rowHook?: (row: any, addError: (fieldKey: string, error: any) => void, table: any[]) => any | Promise<any>;
  private tableHook?: (
    table: any[],
    addError: (rowIndex: number, fieldKey: string, error: any) => void
  ) => any[] | Promise<any[]>;
  private onBack: () => void;
  private registeredAddons: BaseAddon[] = [];
  private existingData: any[] = [];
  private relationData: Record<string, any[]> = {};

  constructor(onNext: (event: StepChangeEvent) => void, onBack: () => void, params: ValidateDataAddonParams<T>) {
    super("ValidateDataAddon", [StepType.validateData], onNext, params);
    this.onBack = onBack;
    this.rowHook = params.rowHook;
    this.tableHook = params.tableHook;
    if (params.registeredAddons) {
      this.registeredAddons = params.registeredAddons;
    }
  }

  getConflictFields(): string[] {
    const conflictFields: string[] = [];

    for (const addon of this.registeredAddons) {
      if (addon.conflictFields && addon.conflictFields.length > 0) {
        conflictFields.push(...addon.conflictFields);
      }
    }

    return conflictFields;
  }

  private loadExistingDataFromAddons = async (): Promise<{
    existingData: any[];
    relationData: Record<string, any[]>;
  }> => {
    let existingData: any[] = [];
    let relationData: Record<string, any[]> = {};

    for (const addon of this.registeredAddons) {
      if (addon.getExistingData) {
        try {
          const data = await addon.getExistingData();
          if (data && data.length > 0) {
            existingData = [...data];
          }
        } catch (error) {
          console.error(`Error loading data from ${addon.name}:`, error);
        }
      }

      if (addon.getExistingRelationData) {
        try {
          const addonRelationData = await addon.getExistingRelationData();
          relationData = { ...relationData, ...addonRelationData };
        } catch (error) {
          console.error(`Error loading relation data from ${addon.name}:`, error);
        }
      }
    }

    this.existingData = existingData;
    this.relationData = relationData;
    return { existingData, relationData };
  };

  protected getJSXElement(step: StepType, state: any): React.ReactElement {
    if (!state.data) {
      return <NoDataFound onBack={this.onBack}></NoDataFound>;
    }

    return (
      <ValidateDataView
        data={state.data || []}
        columns={state.columns || {}}
        headerValues={state.headerValues || []}
        fields={this.fields}
        entityRelations={state.entityRelations || {}}
        onValidate={this.handleValidate}
        onCellEdit={this.handleCellEdit}
        onRowDelete={this.handleRowDelete}
        onAddRow={this.handleAddRow}
        onSubmit={this.handleSubmit}
        onBack={this.onBack}
        rowHook={this.rowHook}
        tableHook={this.tableHook}
        onLoadExistingData={this.loadExistingDataFromAddons}
        existingData={this.existingData}
        conflictFields={this.getConflictFields()}
      />
    );
  }

  /**
   * Enhanced email validation function
   */
  private isValidEmail = (email: string): boolean => {
    if (!email || typeof email !== "string") return false;

    const trimmedEmail = email.trim();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

    const invalidPatterns = [
      /\.{2,}/, // consecutive dots
      /^\./, // starts with dot
      /\.$/, // ends with dot
      /@\./, // @ followed by dot
      /\.@/, // dot followed by @
      /@.*@/ // multiple @ symbols
    ];

    if (!emailRegex.test(trimmedEmail)) {
      return false;
    }

    for (const pattern of invalidPatterns) {
      if (pattern.test(trimmedEmail)) {
        return false;
      }
    }

    return true;
  };

  private hasUnsupportedCharacters = (text: string): boolean => {
    if (!text || typeof text !== "string") return false;

    const allowedPattern = /^[a-zA-Z0-9\s.,'`\-_&()]+$/;
    
    return !allowedPattern.test(text);
  };

  private handleValidate = async (data: any[]): Promise<ValidationResult> => {
    const errors: ValidationError[] = [];
    const warnings: ValidationWarning[] = [];

    data.forEach(row => {
      this.fields.forEach(field => {
        if (field.type === "email") {
          const emailValue = row[field.key];
          if (field.required && (!emailValue || emailValue === "")) {
            errors.push({
              rowId: row.id,
              field: field.key,
              message: `${field.name} is required`,
              code: ValidationErrorCode.MISSING_EMAIL
            });
          } else if (emailValue && !this.isValidEmail(emailValue)) {
            errors.push({
              rowId: row.id,
              field: field.key,
              message: `Invalid email format: ${emailValue}`,
              code: ValidationErrorCode.INVALID_EMAIL
            });
          }
        } else if (field.required && (!row[field.key] || row[field.key] === "")) {
          errors.push({
            rowId: row.id,
            field: field.key,
            message: `${field.name} is required`,
            code: ValidationErrorCode.REQUIRED_FIELD
          });
        }

        if (field.type === "text" && row[field.key]) {
          const textValue = row[field.key];
          if (textValue && this.hasUnsupportedCharacters(textValue)) {
            errors.push({
              rowId: row.id,
              field: field.key,
              message: `${field.name} contains invalid characters. Only letters, numbers, spaces, and common name symbols (. , ' \` - _ & ( )) are allowed.`,
              code: ValidationErrorCode.UNSUPPORTED_CHARACTERS
            });
          }
        }
      });
    });

    const primaryKeyField = this.fields.find(f => f.required)?.key;
    const seen = new Map<string, number[]>();

    data.forEach(row => {
      const keyValue = row[primaryKeyField];
      if (keyValue) {
        if (!seen.has(keyValue)) {
          seen.set(keyValue, []);
        }
        seen.get(keyValue)!.push(row.id);
      }
    });

    seen.forEach((rowIds, keyValue) => {
      if (rowIds.length > 1) {
        const duplicateIds = rowIds.slice(1);

        duplicateIds.forEach(rowId => {
          warnings.push({
            rowId,
            field: primaryKeyField,
            message: `Duplicate ${primaryKeyField}: "${keyValue}" - original in row ${rowIds[0]}`,
            code: ValidationWarningCode.DUPLICATE_VALUE
          });
        });
      }
    });

    return {
      valid: errors.length === 0,
      errors,
      warnings
    };
  };

  private handleCellEdit = async (rowIndex: number, fieldKey: string, newValue: any): Promise<void> => {
    // TODO: Implement cell edit logic
  };

  private handleRowDelete = async (rowIndex: number): Promise<void> => {
  };

  private handleAddRow = async (): Promise<void> => {
    // TODO: Implement add row logic
  };

  private handleSubmit = async (submissionData: UploaderSubmissionData<T>): Promise<void> => {
    try {
      const validationResult = await this.handleValidate(submissionData.rows);

      if (!validationResult.valid) {
        toastService
          .showErrorToast(TOASTER_TOAST_TIME.NORMAL, "right")
          .setContent(<span>Please fix validation errors before submitting</span>);
        return;
      }

      if (this.onSubmit) {
        await this.onSubmit(submissionData);
      }

      this.onNext({
        type: StepType.validateData,
        data: submissionData.rows
      });
    } catch (error) {
      console.error("Error submitting data:", error);
      toastService
        .showErrorToast(TOASTER_TOAST_TIME.NORMAL, "right")
        .setContent(<span>{error?.message || "Error submitting data"}</span>);
    }
  };
}
